import React, { useEffect, useRef } from 'react'

import { conicWash } from '../orbPalettes'

// How fast the measured level chases the raw reading, per frame. Speech RMS is
// spiky; unsmoothed, the state would flap between `speaking` and `idle` inside
// a single word.
const SMOOTHING = 0.18

// Separate on/off thresholds so a level hovering at the boundary can't oscillate
// the state — and with it, restart the CSS animations — several times a second.
const SPEAK_ON = 0.1
const SPEAK_OFF = 0.05
const LISTEN_ON = 0.12
const LISTEN_OFF = 0.06

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// The agent, as a soft organic mass that keeps moving (PIB-544).
//
// The motion is entirely CSS — a rotating conic layer, three heavily blurred
// radial lobes drifting on loops of different lengths, and a slow border-radius
// morph so the silhouette breathes. Nothing here animates from JavaScript.
//
// What JavaScript does is decide *which* of the three intensities is playing,
// by writing `data-state` on the container. The state comes from the call's own
// audio rather than a guess: the agent's output leg drives `speaking`, the
// operator's mic drives `listening`. `data-state` is a DOM attribute write, not
// React state, so choosing it costs no re-render — and it only changes at the
// boundaries below, not per frame.
const CallOrb = ({ palette, getLevels, animating, size, children }) => {
  const rootRef = useRef(null)

  useEffect(() => {
    const node = rootRef.current
    if (!node) {
      return undefined
    }
    // Reduced motion is handled in CSS (every animation is frozen and the
    // gradient held still), but the state still has to be *set* once so the
    // static look is the calm one.
    if (!animating || !getLevels || prefersReducedMotion()) {
      node.setAttribute('data-state', 'idle')
      return undefined
    }

    let handle = null
    let output = 0
    let input = 0
    let state = 'idle'

    const frame = () => {
      const levels = getLevels()
      output += (levels.output - output) * SMOOTHING
      input += (levels.input - input) * SMOOTHING

      // The agent talking wins over the operator talking: during a barge-in
      // both legs are hot, and the orb belongs to the agent.
      let next = state
      if (output > SPEAK_ON) {
        next = 'speaking'
      } else if (input > LISTEN_ON) {
        next = 'listening'
      } else if (
        (state === 'speaking' && output < SPEAK_OFF && input < LISTEN_ON) ||
        (state === 'listening' && input < LISTEN_OFF)
      ) {
        next = 'idle'
      }

      if (next !== state) {
        state = next
        node.setAttribute('data-state', state)
      }
      handle = requestAnimationFrame(frame)
    }

    node.setAttribute('data-state', 'idle')
    handle = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(handle)
  }, [animating, getLevels])

  return (
    <div
      className="call-orb"
      data-testid="call-orb"
      data-state="idle"
      // The blob is the same object wherever it appears; only its size changes.
      style={size ? { '--orb-size': size } : undefined}
      ref={rootRef}
    >
      {/* Outside the clipped body, because a ring that marks "I can hear you"
          has to read as something around the blob rather than inside it. */}
      <span className="call-orb__ring" aria-hidden="true" />

      {/* The clip. Its border-radius is animated rather than fixed at 50%, so
          the silhouette is an organic blob that breathes instead of a disc. */}
      <span className="call-orb__body" aria-hidden="true">
        <span className="call-orb__conic" style={{ background: conicWash(palette, 0) }} />
        {/* Three lobes on loops of 11s / 17s / 23s. Coprime-ish durations so
            the combined motion has no visible period — a single loop, however
            long, eventually reads as a repeating animation. */}
        <span
          className="call-orb__lobe call-orb__lobe--1"
          style={{ background: `radial-gradient(circle, ${palette.deep} 0%, transparent 68%)` }}
        />
        <span
          className="call-orb__lobe call-orb__lobe--2"
          style={{ background: `radial-gradient(circle, ${palette.mid} 0%, transparent 66%)` }}
        />
        <span
          className="call-orb__lobe call-orb__lobe--3"
          style={{ background: `radial-gradient(circle, ${palette.light} 0%, transparent 70%)` }}
        />
      </span>

      {children}
    </div>
  )
}

export default CallOrb
